/* Settings -> one scope's registry rows (spec 2026-10-18-full-separation §4.5, §7.2): a
 * generic row per setting the server answers -- the value, inherited or own, a reset, the
 * help text in the page's language and, on a row that is not editable, the server's
 * reason. The server owns every rule (kind, editable, why, masking); this file renders.
 * A secret's input is never pre-filled: its display is the placeholder, and the server
 * refuses that display written back (app/routers/settings.py masked_echo).
 *
 * Imported by settings.jsx (esbuild bundles it into settings.js); also window.ScopeSettings.
 */
import { confirmAction } from './feedback.jsx';
import { badgeOf, canReset, draftOf, isDefault, maskText, sections, sendable } from './settings-scope-logic.mjs';

const { useState, useEffect, useRef, useCallback } = React;

const T = {
  en: {
    title: 'Company settings', loading: 'Loading…', save: 'Save', reset: 'Reset to the installation value',
    notSet: 'not set', empty: 'Nothing to show.',
    badge: { own: 'own', inherited: 'inherited', default: 'default', set: 'set' },
    changed: (by, at) => `changed by ${by}${at ? ` at ${at.replace('T', ' ').slice(0, 16)} UTC` : ''}`,
    defaults: 'Defaults for every company',
    defaultsNote: "A company without a value of its own uses these. A company's own value wins until it is reset.",
    tenantNote: 'Each setting shows whether this company has its own value or inherits the installation default. Reset removes the company\'s own value.',
    confirmReset: (k) => `Remove this company's own ${k}? It then uses the installation's.`,
    section: {
      keys: 'Keys', models: 'Models', agent: 'Agent', mirror: 'Mirror', workbench: 'Workbench',
      writejobs: 'Write jobs', channels: 'Mail and Telegram', voice: 'Voice', access: 'Access',
      privacy: 'Privacy', remote: 'Remote MCP',
    },
  },
  he: {
    title: 'הגדרות החברה', loading: 'טוען…', save: 'שמירה', reset: 'חזרה לערך ההתקנה',
    notSet: 'לא מוגדר', empty: 'אין מה להציג.',
    badge: { own: 'של החברה', inherited: 'בירושה', default: 'ברירת מחדל', set: 'מוגדר' },
    changed: (by, at) => `שונה על ידי ${by}${at ? ` ב-${at.replace('T', ' ').slice(0, 16)} UTC` : ''}`,
    defaults: 'ברירות מחדל לכל החברות',
    defaultsNote: 'חברה בלי ערך משלה משתמשת בערכים האלה. ערך של החברה גובר עד שמאפסים אותו.',
    tenantNote: 'כל הגדרה מראה אם לחברה יש ערך משלה או שהיא יורשת את ברירת המחדל של ההתקנה. איפוס מוחק את הערך של החברה.',
    confirmReset: (k) => `להסיר את הערך של החברה עבור ${k}? החברה תשתמש אז בערך של ההתקנה.`,
    section: {
      keys: 'מפתחות', models: 'מודלים', agent: 'סוכן', mirror: 'מראה', workbench: 'שולחן עבודה',
      writejobs: 'עבודות כתיבה', channels: 'דואר וטלגרם', voice: 'קול', access: 'גישה',
      privacy: 'פרטיות', remote: 'MCP מרוחק',
    },
  },
};

function errText(e) {
  const d = e && e.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && !Array.isArray(d) && d.message) return d.message;
  if (Array.isArray(d)) return d.map((i) => String(i.msg)).join('; ');
  return String((e && e.message) || e);
}

function SettingRow({ row, scope, lang, onChanged }) {
  const t = T[lang] || T.en;
  const [draft, setDraft] = useState(() => draftOf(row));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setDraft(draftOf(row)); setError(''); }, [scope, row.key, row.text, row.source, row.updated_at]);
  const id = `setting-${scope}-${row.key}`;
  const help = lang === 'he' ? (row.help_he || row.help_en) : (row.help_en || row.help_he);
  const badge = badgeOf(row, scope);
  const run = (p) => {
    setBusy(true);
    setError('');
    p.then(() => { if (row.secret) setDraft(''); return onChanged(); })
      .catch((e) => setError(errText(e))).finally(() => setBusy(false));
  };
  const save = () => run(PCMAdapter.putSetting(scope, row.key, draft));
  const reset = async () => {
    if (row.secret && !await confirmAction(t.confirmReset(row.key), { lang, danger: true, confirmLabel: t.reset })) return;
    run(PCMAdapter.resetSetting(scope, row.key));
  };
  const multiline = !row.secret && /^[[{]/.test(row.text || '');
  const input = row.secret
    ? <input id={id} className="set-input" type="password" autoComplete="new-password" dir="ltr" disabled={busy} value={draft}
             placeholder={maskText(row.value) || t.notSet} onChange={(e) => setDraft(e.target.value)}/>
    : multiline
      ? <textarea id={id} className="set-input mono" dir="ltr" rows={4} disabled={busy} value={draft}
                  onChange={(e) => setDraft(e.target.value)}/>
      : <input id={id} className="set-input mono" dir="ltr" disabled={busy} value={draft} onChange={(e) => setDraft(e.target.value)}/>;
  return (
    <div className="set-row" data-setting={row.key}>
      <div>
        <label className="set-field-label" htmlFor={row.editable ? id : undefined}>
          <span className="mono" dir="ltr">{row.key}</span>{' '}
          <span className={`chip ${badge === 'own' || badge === 'set' ? 'st-info' : 'st-neutral'}`}>{t.badge[badge]}</span>
        </label>
        {help && <p className="set-note" dir="auto">{help}</p>}
      </div>
      {row.editable ? (
        <>
          {input}
          <button type="button" className="btn btn-sm" disabled={busy || !sendable(row, draft)} onClick={save}>{t.save}</button>
          {canReset(row, scope) && (
            <button type="button" className="btn btn-sm" disabled={busy} onClick={reset}>{t.reset}</button>
          )}
        </>
      ) : (
        <>
          <code className="mono" dir="ltr">{row.secret ? (maskText(row.value) || t.notSet) : (row.text || '—')}</code>
          {row.why && <p className="set-note" dir="auto">{row.why}</p>}
        </>
      )}
      {row.updated_by && <span className="set-status" dir="auto">{t.changed(row.updated_by, row.updated_at)}</span>}
      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
    </div>
  );
}

function ScopeSettings({ lang, scope, defaultsOnly = false }) {
  const t = T[lang] || T.en;
  const key = String(scope);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // The answer for scope A must not land after a switch to B (companies.jsx's live-tenant ref).
  const live = useRef(key);
  live.current = key;
  const request = useRef(0);
  const load = useCallback(() => {
    // A save finishing in an old scope must not supersede the new scope's read.
    if (live.current !== key) return Promise.resolve();
    const asked = key;
    const version = ++request.current;
    return PCMAdapter.getSettings(asked)
      .then((d) => { if (live.current === asked && version === request.current) { setData(d); setError(''); } })
      .catch((e) => { if (live.current === asked && version === request.current) { setData({ scope: asked, settings: [] }); setError(errText(e)); } });
  }, [key]);
  useEffect(() => {
    setData(null); setError(''); load();
    return () => { ++request.current; };
  }, [load]);
  if (data === null || String(data.scope) !== key) return <div className="card set-section wide">{t.loading}</div>;
  const rows = defaultsOnly ? data.settings.filter((r) => isDefault(r, key)) : data.settings;
  return (
    <>
      <div className="card set-section wide">
        <h3 dir="auto">{defaultsOnly ? t.defaults : t.title}</h3>
        <p className="set-note" dir="auto">{defaultsOnly ? t.defaultsNote : t.tenantNote}</p>
        {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
        {!error && rows.length === 0 && <p className="set-status" dir="auto">{t.empty}</p>}
      </div>
      {sections(rows).map(([id, list]) => (
        <div key={id} className="card set-section wide" data-section={id}>
          <h3 dir="auto">{t.section[id] || id}</h3>
          {list.map((row) => <SettingRow key={`${key}-${row.key}`} row={row} scope={key} lang={lang} onChanged={load}/>)}
        </div>
      ))}
    </>
  );
}

Object.assign(window, { ScopeSettings });
export { ScopeSettings };
